import React, {useState, useEffect} from 'react';
import BoxCard from '../../General/BoxCard';
import ErrorHelper from '../../../service/ErrorHelper';
import {CustomInput, SubmitButton} from '../../General/FormInputs';
import {get, post} from '../../../service/ApiService';
import {OpportunityPlan} from '../../../service/Urls';
import {useForm, useFieldArray} from 'react-hook-form';
import {useClientOpp} from '../../../views/Maintenance/OppPlanMaintenance';
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  IconButton,
  Radio,
  RadioGroup,
  Table,
  TableContainer,
  TableCaption,
  Thead,
  Tbody,
  Tfoot,
  Th,
  Tr,
  Td,
} from '@chakra-ui/react';
import {FaPlusSquare, FaTrashAlt} from 'react-icons/fa';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {isAdmin} from '../../../helpers/localStorage';

export default function OppTargetNew() {
  const {client} = useClientOpp(null);

  const readOnly = client?.client?.id == null;

  const [surveyType, setSurveyType] = useState('1');
  const [surveyTypeAvailability, setSurveyTypeAvailability] = useState([]);

  const [toast, setToast] = useState('');

  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: {errors},
  } = useForm();

  const {
    fields: callTypes,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'callTypes',
  });

  const {data} = useQuery(
    ['targets', client?.client?.id],
    async () => {
      const oppClient = await get(OpportunityPlan.GET_BY_ID + '/' + client.client.id);

      setSurveyTypeAvailability([
        oppClient?.clFuneral ?? false,
        oppClient?.clCremat ?? false,
        oppClient?.clCemet ?? false,
        oppClient?.clMonu ?? false,
        oppClient?.clPreneed ?? false,
        oppClient?.clPet ?? false,
        oppClient?.clEvent ?? false,
      ]);

      return get(OpportunityPlan.RECORD + '/' + oppClient?.id + '/targets');
    },
    {
      onError: () => {
        setToast({error: 'Error loading targets'});
      },
      enabled: !!client?.client?.id,
    }
  );

  const {mutate: update, isLoading: isUpdating} = useMutation(() => post(OpportunityPlan.MISSING_TARGETS), {
    onSuccess: result => {
      if (result.error) {
        setToast({response: {error: true}, errorMessage: result.error});
      } else {
        setToast({successMessage: 'Successfully updated targets.'});

        queryClient.invalidateQueries(['targets']);
      }
    },
    onError: () => {
      setToast({response: {error: true}, errorMessage: 'Error updating targets.  Please try again.'});
    },
  });

  const {mutate: save, isLoading: isSaving} = useMutation(
    data => {
      const parsedData = {
        ...data,
        callTypes: data.callTypes.map(callType => ({
          ...callType,
          ctType: callType.ctType?.toString(),
          ctPrt: callType.ctPrt?.toString(),
          ctGai: callType.ctGai?.toString(),
          ctGrp: callType.ctGrp?.toString(),
        })),
      };

      return post(OpportunityPlan.RECORD + '/' + data.clientOId + '/targets/' + data.id, parsedData);
    },
    {
      onSuccess: result => {
        if (result.error) {
          setToast({response: {error: true}, errorMessage: result.error});
        } else {
          setToast({successMessage: 'Successfully saved targets.'});

          queryClient.invalidateQueries(['targets']);
        }
      },
      onError: () => {
        setToast({response: {error: true}, errorMessage: 'Error saving targets.  Please try again.'});
      },
    }
  );

  useEffect(() => {
    const targets = data?.data.find(x => x.serviceType === parseInt(surveyType))?.targets[0];

    if (targets) {
      reset({
        ...targets,
        callTypes: targets.callTypes?.map(callType => ({
          nseq: 0,
          ctType: '',
          ctPrt: 0,
          ctGai: 0,
          ctGrp: 0,
          ctTarget: 0,
          ctMix: 0,
          ctAlias: '',
          ...callType,
        })),
      });
    }
  }, [data, surveyType]);

  return (
    <form w="100%" onSubmit={handleSubmit(save)}>
      <Flex w="90%" flexDir={{base: 'column', lg: 'row'}}>
        <Box w="100%">
          <BoxCard>
            <Flex>
              <Heading fontSize="xl" flex={1}>
                Survey Type
              </Heading>
              <Button colorScheme="secondary" onClick={() => update()} disabled={isUpdating}>
                Update
              </Button>
            </Flex>
            <RadioGroup onChange={setSurveyType} value={surveyType} isDisabled={!client?.client?.id}>
              <Flex flexDirection={'row'} justify={'center'}>
                {[
                  {key: 'Funeral', value: '1'},
                  {key: 'Cremation', value: '2'},
                  {key: 'Cemetery', value: '4'},
                  {key: 'Monument', value: '8'},
                  {key: 'PreNeed', value: '16'},
                  {key: 'Pet Cremation', value: '32'},
                  {key: 'Event Planning', value: '64'},
                ].map((item, idx) => (
                  <Radio value={item.value} colorScheme={'tertiary'} mx="2" key={idx} isDisabled={!surveyTypeAvailability[idx]}>
                    {item.key}
                  </Radio>
                ))}
              </Flex>
            </RadioGroup>
            <Divider my="2" />
            <Box w="100%" my="2" mb="4">
              <Flex w="100%">
                <CustomInput
                  id="ctAncalls"
                  w="50%"
                  requireInt
                  min="0"
                  type="number"
                  isReadOnly={readOnly}
                  name="Target AtNeed Calls"
                  register={register}
                  errors={errors}
                />
                <CustomInput
                  id="ctPncalls"
                  w="50%"
                  requireInt
                  min="0"
                  type="number"
                  isReadOnly={readOnly}
                  name="Target PreNeed Calls"
                  register={register}
                  errors={errors}
                />
                <CustomInput
                  id="ctPnavg"
                  w="50%"
                  requireInt
                  min="0"
                  type="number"
                  isReadOnly={readOnly}
                  name="Avg Net Pn Contract"
                  register={register}
                  errors={errors}
                />
              </Flex>
            </Box>

            <TableContainer>
              <Table id="arr" width={{base: '100%'}} border="solid 2px #0c5f78" variant="striped" colorScheme="mint" size={'md'}>
                <TableCaption>Target Types</TableCaption>
                <Thead>
                  <Tr border="solid 2px #0c5f78">
                    <Th>Call Type</Th>
                    <Th>Print Seq</Th>
                    <Th>GAI Code</Th>
                    <Th>GRP</Th>
                    <Th>Target Revenue</Th>
                    <Th>Target Mix</Th>
                    <Th>Call Type Alias</Th>

                    {client?.client?.id && isAdmin() ? (
                      <Td>
                        <IconButton
                          colorScheme="tertiary"
                          title="Add"
                          size="md"
                          icon={<FaPlusSquare />}
                          onClick={() =>
                            append({
                              id: '',
                              nseq: 0,
                              ctType: '',
                              ctPrt: 0,
                              ctGai: 0,
                              ctGrp: 0,
                              ctTarget: 0,
                              ctMix: 0,
                            })
                          }
                        />
                      </Td>
                    ) : null}
                  </Tr>
                </Thead>
                <Tbody>
                  {callTypes.map((item, idx) => (
                    <Tr key={item.id}>
                      <Td mt={2}>
                        <CustomInput register={register} errors={errors} id={'callTypes.' + idx + '.ctType'} py={1} />
                      </Td>
                      <Td w="50px">
                        <CustomInput register={register} errors={errors} id={'callTypes.' + idx + '.nseq'} requireInt min="0" max="9" py={1} />
                      </Td>
                      <Td w="50px">
                        <CustomInput register={register} errors={errors} id={'callTypes.' + idx + '.ctGai'} requireInt min="0" max="9" py={1} />
                      </Td>
                      <Td w="50px" px="2">
                        <CustomInput register={register} errors={errors} id={'callTypes.' + idx + '.ctGrp'} requireInt min="0" max="9" py={1} />
                      </Td>
                      <Td w="10%">
                        <CustomInput
                          register={register}
                          errors={errors}
                          id={'callTypes.' + idx + '.ctTarget'}
                          requireInt
                          min="0"
                          max="999999"
                          py={1}
                        />
                      </Td>
                      <Td w="10%">
                        <CustomInput register={register} errors={errors} id={'callTypes.' + idx + '.ctMix'} requireInt min="0" max="999999" py={1} />
                      </Td>
                      <Td>
                        <CustomInput register={register} errors={errors} id={'callTypes.' + idx + '.ctAlias'} py={1} />
                      </Td>
                      {isAdmin() && (
                        <Td>
                          <button type="submit" style={{display: 'none'}} />
                          <IconButton colorScheme="tertiary" title="Delete" size="md" icon={<FaTrashAlt />} onClick={() => remove(idx)} />
                        </Td>
                      )}
                    </Tr>
                  ))}
                </Tbody>
                <Tfoot>
                  <Tr></Tr>
                </Tfoot>
              </Table>
            </TableContainer>
            {isAdmin() && <SubmitButton name="Save" disabled={isSaving} />}
          </BoxCard>
        </Box>
      </Flex>
      <ErrorHelper toastResponse={toast} />
    </form>
  );
}
